"use client"

import { useState, useEffect, useCallback } from "react"
import { ShieldCheck, Fingerprint, KeyRound, X, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { startAuthentication } from "@simplewebauthn/browser"
import type { DestructiveGuardMode } from "@/lib/types/ai-chat"
import { fetchApi } from "@/lib/api/client"
import { StepUpFactorPicker, type StepUpFactor } from "./step-up-factor-picker"
import { useStepUpVerify, useStepUpInitiate } from "@/components/settings/hooks/use-ai-chat"
import { useChatContext } from "@/lib/ai-chat/chat-context"

// ── Props ────────────────────────────────────────────────────────────────────

type BaseProps = {
  open: boolean
  onOpenChange: (v: boolean) => void
  toolName: string
  input: Record<string, unknown>
  conversationId: string
  onConfirm: () => Promise<void> | void
  onCancel: () => void
}

export type VerificationDialogProps = BaseProps &
  (
    | { requirement: "confirm"; message?: string }
    | {
        requirement: "step-up"
        factors: StepUpFactor[]
        verifyReason: string
        passkeyOptions?: unknown
      }
  )

// ── Component ────────────────────────────────────────────────────────────────

export function VerificationDialog(props: VerificationDialogProps) {
  const { open, onOpenChange, toolName, onConfirm, onCancel } = props

  // Step-up path hooks
  const stepUpVerify = useStepUpVerify()
  // Fetch server-issued WebAuthn options before opening the authenticator.
  // Root cause: passkeyOptions was never fetched — startAuthentication received undefined.
  // Call useStepUpInitiate on mount whenever step-up + passkey are required,
  // then forward the returned passkeyOptions to the picker. The assertion sent
  // back does not echo a client-controlled expected challenge.
  const stepUpInitiate = useStepUpInitiate()
  const { setStepUpToken } = useChatContext()

  // ── Step-up passkey options state ────────────────────────────────────────
  const [fetchedPasskeyOptions, setFetchedPasskeyOptions] = useState<Record<string, unknown> | undefined>(undefined)

  // ── Confirm-tier state ────────────────────────────────────────────────────
  const [mode, setMode] = useState<DestructiveGuardMode>("confirmation_code")
  const [hasPasskeys, setHasPasskeys] = useState(false)
  const [code, setCode] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [settingsLoaded, setSettingsLoaded] = useState(false)

  // Fetch passkey challenge on mount when step-up + passkey factor is required.
  // Without this, passkeyOptions is always undefined and startAuthentication fails.
  useEffect(() => {
    if (!open || props.requirement !== "step-up") return
    if (!props.factors.includes("passkey")) return
    setFetchedPasskeyOptions(undefined)
    stepUpInitiate.mutate(
      { toolName, input: props.input, conversationId: props.conversationId },
      {
        onSuccess: (data) => {
          if (data.passkeyOptions) {
            setFetchedPasskeyOptions(data.passkeyOptions as Record<string, unknown>)
          }
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.requirement])

  // Fetch settings only when showing the confirm-tier variant
  useEffect(() => {
    if (!open || props.requirement !== "confirm") return
    setCode("")
    setError("")
    setLoading(false)
    const settingsUrl = "/settings/ai-chat"
    fetchApi<{ destructiveGuardMode?: DestructiveGuardMode; passkeys?: unknown[] }>(settingsUrl)
      .then((s) => {
        setMode(s.destructiveGuardMode || "confirmation_code")
        setHasPasskeys((s.passkeys || []).length > 0)
        setSettingsLoaded(true)
      })
      .catch(() => setSettingsLoaded(true))
  }, [open, props.requirement])

  // Obtain the server-issued approval for the Confirm tier and hand it to the
  // executor out-of-band.
  //
  // The old gate was `confirmed: true` inside the tool ARGUMENTS — a field the
  // model writes. The model could therefore approve its own destructive action,
  // and a prompt injection could start a campaign with no human involved. The
  // token below is minted server-side and bound to this exact tool + input, so
  // it authorises one action and nothing else.
  const approveConfirmTier = useCallback(async () => {
    const data = await stepUpInitiate.mutateAsync({
      toolName,
      input: props.input,
      conversationId: props.conversationId,
    })
    if (!data.confirmationToken || !data.confirmationExpiresAt) {
      // Fail closed and say so, rather than proceeding and letting the executor
      // refuse with a confusing downstream error.
      throw new Error("Approval could not be issued — please try again.")
    }
    setStepUpToken(data.confirmationToken, data.confirmationExpiresAt)
  }, [stepUpInitiate, toolName, props.input, props.conversationId, setStepUpToken])

  const handleCodeVerify = useCallback(async () => {
    if (!code.trim()) {
      setError("Please enter the confirmation code")
      return
    }
    setLoading(true)
    setError("")
    try {
      const verifyUrl = "/settings/ai-chat/verify"
      const data = await fetchApi<{ verified: boolean }>(verifyUrl, {
        method: "POST",
        body: JSON.stringify({ mode: "code", code: code.trim() }),
      })
      if (data.verified) {
        await approveConfirmTier()
        await onConfirm()
        onOpenChange(false)
      } else {
        setError("Invalid confirmation code. Please try again.")
      }
    } catch {
      setError("Verification failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [code, approveConfirmTier, onConfirm, onOpenChange])

  const handlePasskeyVerify = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      // Step 1: Get authentication options from the server
      const authOptsUrl = "/settings/ai-chat/passkey/auth-options"
      const options = await fetchApi<Record<string, unknown>>(authOptsUrl, {
        method: "POST",
      })

      // Step 2: Prompt the user for biometric (Touch ID / Face ID)
      const credential = await startAuthentication({ optionsJSON: options as unknown as import('@simplewebauthn/browser').StartAuthenticationOpts['optionsJSON'] })

      // Step 3: Verify the assertion on the server
      const passkeyVerifyUrl = "/settings/ai-chat/verify"
      const verifyData = await fetchApi<{ verified: boolean }>(passkeyVerifyUrl, {
        method: "POST",
        body: JSON.stringify({ mode: "passkey", credential }),
      })

      if (verifyData.verified) {
        await approveConfirmTier()
        await onConfirm()
        onOpenChange(false)
      } else {
        setError("Passkey verification failed. Try again or use confirmation code.")
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Passkey verification failed"
      if (msg.includes("Cancelled") || msg.includes("AbortError") || msg.includes("NotAllowedError")) {
        setError("Verification cancelled.")
      } else {
        setError("Passkey verification failed. Try using confirmation code instead.")
        setMode("confirmation_code")
      }
    } finally {
      setLoading(false)
    }
  }, [approveConfirmTier, onConfirm, onOpenChange])



  // ── Step-up submit handler ────────────────────────────────────────────────

  const handleStepUpSubmit = useCallback(
    async (factor: StepUpFactor, payload: Record<string, unknown>) => {
      // `input` is load-bearing: the server binds the issued token to this
      // exact tool + arguments. Without it the token is UNBOUND and the
      // executor refuses it — one approval must not authorise every other
      // Verify-tier tool for the next five minutes.
      const result = await stepUpVerify.mutateAsync({
        factor,
        payload,
        toolName,
        input: props.input,
      })
      setStepUpToken(result.stepUpToken, result.expiresAt)
      await onConfirm()
      onOpenChange(false)
    },
    [stepUpVerify, toolName, props.input, setStepUpToken, onConfirm, onOpenChange],
  )

  if (!open) return null

  const toolDisplayName = toolName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

  // ── Step-up branch ────────────────────────────────────────────────────────

  if (props.requirement === "step-up") {
    // While the initiate call is in-flight, show a spinner so the user
    // doesn't click "Verify with passkey" before passkeyOptions arrives.
    const needsPasskeyOptions = props.factors.includes("passkey")
    const passkeyOptionsReady = !needsPasskeyOptions || fetchedPasskeyOptions !== undefined
    const initiating = needsPasskeyOptions && stepUpInitiate.isPending

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-destructive/10">
                <ShieldCheck className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-base font-semibold">Identity Verification</h3>
                <p className="text-xs text-muted-foreground">Required for this operation</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            <div className="px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20 text-sm">
              <span className="font-medium">Operation:</span>{" "}
              <span className="text-muted-foreground">{toolDisplayName}</span>
            </div>

            {initiating ? (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Preparing verification…
              </div>
            ) : (
              <StepUpFactorPicker
                factors={props.factors}
                verifyReason={props.verifyReason}
                // Use freshly fetched passkeyOptions; fall back to the prop only if initiate
                // was not needed (non-passkey factors).
                passkeyOptions={passkeyOptionsReady ? fetchedPasskeyOptions : undefined}
                onSubmit={handleStepUpSubmit}
                onCancel={onCancel}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Confirm branch (existing UX — preserved) ──────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-destructive/10">
              <ShieldCheck className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Identity Verification</h3>
              <p className="text-xs text-muted-foreground">Required for destructive operations</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {props.message && (
            <div className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
              {props.message}
            </div>
          )}

          <div className="px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20 text-sm">
            <span className="font-medium">Operation:</span>{" "}
            <span className="text-muted-foreground">{toolDisplayName}</span>
          </div>

          {settingsLoaded && mode === "confirmation_code" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Enter your personalized confirmation code to authorize this operation.
              </p>
              <div className="flex gap-2">
                <Input
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value)
                    setError("")
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleCodeVerify()}
                  placeholder="Enter confirmation code"
                  className="font-mono tracking-wider"
                  autoFocus
                />
                <Button onClick={handleCodeVerify} disabled={loading} className="shrink-0">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          {settingsLoaded && mode === "passkey" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Use your device&apos;s biometric authentication (Touch ID, Face ID, or security key) to verify your identity.
              </p>
              <Button
                onClick={handlePasskeyVerify}
                disabled={loading}
                className="w-full gap-2"
                size="lg"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Fingerprint className="h-5 w-5" />
                )}
                {loading ? "Verifying..." : "Verify with Touch ID"}
              </Button>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive font-medium">{error}</p>
          )}

          {/* Mode toggle — only show passkey option if passkeys are registered */}
          {settingsLoaded && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setMode(mode === "confirmation_code" ? "passkey" : "confirmation_code")}
            >
              {mode === "confirmation_code"
                ? hasPasskeys
                  ? "Use passkey instead"
                  : "Register a passkey in AI Chat Settings to use biometric verification"
                : "Use confirmation code instead"}
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}
